import IOrder from "@models/IOrder";
import { useContext, useMemo } from "react";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import IOwTableCols from "@models/IOwTableCols";
import { FormattedNumber } from "react-intl";
import SupplierContext from "@contexts/SupplierContext";
import IOrder_Item from "@models/IOrder_Item";
import ICategory from "@models/ICategory";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import "./CategoryTallyWidget.scss";
import DimensionUnitsEnum from "@models/DimensionUnitsEnum";
import { convertDimension, formatDimensionDisplay } from "@utils/DimensionConversion";

type Props = {
    order: IOrder;
};

interface ICategoryTally {
    categoryName: string;
    totalQty: number;
    totalDepth: number;
    totalDepthInFeet: number;
}

export const CategoryTallyWidget = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    // Create a stable dependency for order items changes
    const orderItemsKey = useMemo(() => {
        if (!props.order?.Order_Items) return "";
        return props.order.Order_Items.map((item) => `${item.id || item.guid}-${item.qty}-${item.depth}-${item.catalog_item_id}`).join(",");
    }, [props.order?.Order_Items]);

    const categoryTallies = useMemo(() => {
        const tallies: { [key: string]: ICategoryTally } = {};

        // Check if order has items
        if (!props.order?.Order_Items) {
            return [];
        }

        // Process each order item
        props.order.Order_Items.forEach((item: IOrder_Item) => {
            // Check if item has catalog item with category
            if (item.Catalog_Item?.Category) {
                const category: ICategory = item.Catalog_Item.Category;

                // Only process categories that have perform_tally set to true
                if (category.perform_tally) {
                    const categoryKey = category.id?.toString() || category.guid || "";

                    if (!tallies[categoryKey]) {
                        tallies[categoryKey] = {
                            categoryName: category.name || "",
                            totalQty: 0,
                            totalDepth: 0,
                            totalDepthInFeet: 0,
                        };
                    }

                    // Add quantity
                    tallies[categoryKey].totalQty += item.qty || 0;

                    // Add depth if dimensions are enabled
                    if (supplierModel.has_dimensions && item.depth) {
                        const depthValue = item.depth || 0;
                        const qty = item.qty || 0;
                        const totalDepthForItem = depthValue * qty;

                        // Get the dimension unit from the catalog item (default to inches if not specified)
                        const dimensionUnitId = item.Catalog_Item?.dimension_unit_id || DimensionUnitsEnum.INCHES;

                        // Convert to feet for consistent display
                        const depthInFeet = convertDimension(totalDepthForItem, dimensionUnitId, DimensionUnitsEnum.FEET);

                        tallies[categoryKey].totalDepth += totalDepthForItem;
                        tallies[categoryKey].totalDepthInFeet += depthInFeet;
                    }
                }
            }
        });

        // Convert to array and sort by category name
        return Object.values(tallies).sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    }, [orderItemsKey, supplierModel.has_dimensions]);

    const renderNumber = (value: number) => {
        // Format as feet and inches (value is already in feet from totalDepthInFeet)
        const formatted = formatDimensionDisplay(value, DimensionUnitsEnum.FEET);
        return <span>{formatted || "0'"}</span>;
    };

    const renderQty = (value: number) => {
        return <FormattedNumber value={value || 0} maximumFractionDigits={0} minimumFractionDigits={0} />;
    };

    // Define columns
    const columns: IOwTableCols[] = [
        {
            title: "category",
            valueProp: "categoryName",
        },
        {
            title: "total_qty",
            valueProp: "totalQty",
            renderFn: renderQty,
            className: "text-end",
        },
    ];

    // Add depth column if dimensions are enabled
    if (supplierModel.has_dimensions) {
        columns.push({
            title: supplierModel.length_for_depth ? "total_length_ft" : "total_depth_ft",
            valueProp: "totalDepthInFeet",
            renderFn: renderNumber,
            className: "text-end",
        });
    }

    return (
        <div className={"CategoryTallyWidget"}>
            {categoryTallies.length > 0 ? (
                <OwFnTable
                    columns={columns}
                    dataSet={categoryTallies}
                    dataFetch={false}
                    initialData={false}
                    wrappingClassName={"rounded"}
                    tableClassName="rounded"
                />
            ) : (
                <div className={"empty-state"}>
                    <OwLabel title={"no_categories_to_tally"} />
                </div>
            )}
        </div>
    );
};
